// Connections Service - Firestore Integration
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where,
  serverTimestamp,
  onSnapshot
} from "firebase/firestore";
import { db } from "@/lib/firebase";

const COLLECTION_NAME = "connections";

// Get all connections for a user
export const getConnections = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId)
    );
    const snapshot = await getDocs(q);
    const connections = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by name on client side
    const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
    
    // Group by type
    return {
      people: connections.filter(c => c.type === "person").sort(sortByName),
      cards: connections.filter(c => c.type === "card").sort(sortByName),
      banks: connections.filter(c => c.type === "bank").sort(sortByName),
      categories: connections.filter(c => c.type === "category").sort(sortByName)
    };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Subscribe to connections (real-time updates)
export const subscribeToConnections = (userId, callback) => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where("userId", "==", userId)
  );
  
  return onSnapshot(q, (snapshot) => {
    const connections = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by name on client side
    const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
    
    callback({
      people: connections.filter(c => c.type === "person").sort(sortByName),
      cards: connections.filter(c => c.type === "card").sort(sortByName),
      banks: connections.filter(c => c.type === "bank").sort(sortByName),
      categories: connections.filter(c => c.type === "category").sort(sortByName)
    });
  }, (error) => {
    // Error silenced for production
  });
};

// Add a new connection (person, card, or bank)
export const addConnection = async (userId, connectionData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...connectionData,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: docRef.id, ...connectionData };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Update a connection
export const updateConnection = async (connectionId, connectionData) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, connectionId);
    await updateDoc(docRef, {
      ...connectionData,
      updatedAt: serverTimestamp()
    });
    return { id: connectionId, ...connectionData };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Delete a connection
export const deleteConnection = async (connectionId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, connectionId));
    return connectionId;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Get connection by ID
export const getConnectionById = async (connectionId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, connectionId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Get people connections only
export const getPeople = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId)
    );
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    // Filter and sort on client side
    return all.filter(c => c.type === "person").sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Get cards connections only
export const getCards = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId)
    );
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    // Filter and sort on client side
    return all.filter(c => c.type === "card").sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Get banks connections only
export const getBanks = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId)
    );
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    // Filter and sort on client side
    return all.filter(c => c.type === "bank").sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Get categories connections only
export const getCategories = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId)
    );
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    // Filtra apenas as categorias e ordena por nome
    return all.filter(c => c.type === "category").sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

export default {
  getConnections,
  subscribeToConnections,
  addConnection,
  updateConnection,
  deleteConnection,
  getConnectionById,
  getPeople,
  getCards,
  getBanks,
  getCategories
};
